(ns hai.tasks.modal
  "The task modals the web-app opens over its pages. Each is the Tasks card
   itself, without the tape that pins it to the dashboard, on a bare dialog,
   holding what the card holds: the new task's ([[hai.web-app.add-menu]]) the
   card's composer ([[hai.tasks.mini-app/composer-view]]), and an open task's
   the card's open task ([[hai.tasks.mini-app/open-task-body]]) under the
   card's header, \"← Back to tasks\" closing the modal, with Open in the ⋮
   beside Delete. It edits for whoever may, and reads for anyone else."
  (:require
   [hai.entity.data :as entity-data]
   [hai.entity.kebab :as kebab]
   [hai.mini-apps.frame :as frame]
   [hai.sharing.helper :as sharing]
   [hai.spaces.data :as spaces-data]
   [hai.tasks.data :as data]
   [hai.tasks.drawer.core :as drawer-core]
   [hai.tasks.eql :as tasks-eql]
   [hai.tasks.header :as header]
   [hai.tasks.mini-app :as tasks-mini-app]
   [hai.tasks.sync-mark :as sync-mark]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defevent defevent-fx defsub]]
   [lambdaisland.ornament :as o]))

(def ^:private open-icon (icons/mui-icon :outlined :open_in_new))

;;
;; The shell
;;

;; Radix wires the dialog's accessible name and description to its own Title
;; and Description; the visible title is the card's header bar, so both are
;; hidden rather than rendered twice.
(def visually-hidden
  {:position "absolute" :width "1px" :height "1px" :padding "0" :margin "-1px"
   :overflow "hidden" :clip "rect(0 0 0 0)" :white-space "nowrap" :border "0"})

;; The card is the modal's surface, so the dialog panel underneath it keeps
;; only its size — no padding and no background of its own. `align=start`
;; top-anchors the dialog (so it grows downward without recentering); the
;; margin-top sits it a comfortable way below the viewport top rather than the
;; ~32px `--space-6` gap that alignment leaves on its own. It overrides only the
;; top of Radix's `margin: auto`, so the dialog stays horizontally centred.
(def bare-dialog-panel
  {:padding "0" :background "transparent" :box-shadow "none"
   :margin-top "10vh"})

(defn keep-escape-in-fields
  "A task modal's `onEscapeKeyDown`. Radix hears Escape first, on the
   document, before any field does; in a field — the title, the description,
   a search — the key is the field's, which closes its list, clears it or
   leaves it, so the modal stays open. Anywhere else it closes the modal."
  [^js e]
  (when (some-> ^js (.-target e) (.closest "input, textarea, [contenteditable='true']"))
    (.preventDefault e)))

;; The duck-tape strip pins a card to the dashboard. A modal floats over the
;; page instead, so it sheds the tape and keeps the rest of the card. Its task
;; fills it as the Home card's does, its body as tall as the Home card's list,
;; so the drawer has the same room; a short window gives it less.
(o/defstyled modal-card tasks-mini-app/card
  [:&::before {:display "none"}]
  [frame/list-clip {:height "min(560px, 70vh)"}])

;;
;; The task open in it
;;

;; The task open in the modal: its uuid, and the task it was opened from to
;; show until a read of it lands.
(defevent-fx ::open [{:keys [db]} task]
  {:db (assoc db ::open {:task-uuid (:task/uuid task) :seed task})
   :fx [[:dispatch [:entity/resolve {:task/uuid (:task/uuid task)} {:pattern tasks-eql/task-eql}]]]})

(defevent ::close [db]
  (dissoc db ::open))

(defsub ::open [db]
  (::open db))

(defn- open-task
  "The Tasks card's open task, under its header as the card has it: \"← Back
   to tasks\", which closes the modal, the save mark for whoever may edit,
   Share, and the ⋮ — Open the task's page, and Delete, disabled, saying why,
   for anyone but its owner. `change-fx` is the host's refresh; every change
   re-reads the task too."
  [{:keys [task change-fx close!]}]
  (let [task-uuid (:task/uuid task)
        can-edit? (sharing/can-edit? task)
        refresh-fx (into [[:dispatch [:entity/resolve {:task/uuid task-uuid} {:pattern tasks-eql/task-eql}]]]
                         change-fx)]
    [modal-card
     [header/bar {:on-back close!}
      (when can-edit?
        [sync-mark/mark {:task task :class "bar-action-btn" :change-fx refresh-fx}])
      [tasks-mini-app/share-button {:drawer-key (drawer-core/drawer-key task-uuid nil)
                                    :can-share? (sharing/can-share? task)}]
      [kebab/menu
       {:actions [["Open" (fn [_]
                            (close!)
                            (>e [:routing/navigate [:tasks/detail task]]))
                   open-icon]]
        :copy-url (kebab/app-url "tasks" task-uuid)
        :on-delete (fn []
                     (close!)
                     (>e [:table/remove-row data/board-collection-id :task/uuid task-uuid])
                     (>e [::data/delete task-uuid change-fx]))
        :delete-label "Delete task"
        :confirm-prompt "Delete this task?"
        :delete-disabled-reason (when-not (sharing/owner? task)
                                  "Only the owner can delete this task")
        :aria-label "Task actions"}]]
     [tasks-mini-app/open-task-body {:task task
                                     :spaces? @(<s [::spaces-data/enabled?])
                                     :on-open-entity entity-data/open-entity
                                     :change-fx refresh-fx
                                     :stamp-fx refresh-fx}]]))

(defn task-modal
  "The task [[::open]] opened, in a modal, for as long as it is open.
   `change-fx` is the host's refresh, riding every change to the task."
  [{:keys [change-fx]}]
  (let [{:keys [task-uuid seed]} @(<s [::open])
        task (when task-uuid
               (or @(<s [:entity {:task/uuid task-uuid}]) seed))
        close! #(>e [::close])]
    [c/DialogRoot
     {:open (some? task)
      :onOpenChange (fn [opening?]
                      (when-not opening?
                        (close!)))}
     [c/DialogContent {:align "start" :max-width "480px" :style bare-dialog-panel
                       ;; Radix would focus the first button in, the save mark,
                       ;; and a focused button shows its tooltip. The dialog
                       ;; takes the focus itself: the keyboard starts inside it,
                       ;; and nothing is named until pointed at.
                       :onOpenAutoFocus (fn [^js e]
                                          (.preventDefault e)
                                          (some-> ^js (.-currentTarget e) .focus))
                       :onEscapeKeyDown keep-escape-in-fields}
      [c/DialogTitle {:style visually-hidden} (or (:task/title task) "Task")]
      [c/DialogDescription {:style visually-hidden} "The task, to read or edit."]
      (when task
        ^{:key (str task-uuid)}
        [open-task {:task task :change-fx change-fx :close! close!}])]]))
